import React, { useState } from "react";
import { useAppDispatch } from "../../../redux/store";

// Import slice - PATH RELATIVI!
import { OdPLegacy, FaseLegacy, deleteOdPLegacy, fetchOdPLegacy, STATI_ODP } from "./odpLegacySlice";
import { OSTACOLO_ICON_MAP } from "../ostacoli/ostacoliSlice";
import { FiltriOdPLegacy } from "components/userPreference/userPreferenceSlice";
import { isTempoNonAttendibile } from "../../common/claire/tempoUtils";

// Auth
import { useAutorizzazioneGruppi } from "../../authentication/useAutorizzazioneGruppi";
import { PermissionTypes } from "../../authentication/permissionsGroups";

// Componenti locali
import OstacoloForm from "../ostacoli/OstacoloForm";
import OperatoreSelectInline from "./OperatoreSelectInline";

// Material UI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {theme} from "../../theme";
import {convertToDateTime} from "../../common/dateUtils/convertToDateTime";
import {DateTime} from "luxon";

// ============================================
// COSTANTI
// ============================================

const getStatoColor = (
    stato?: number
): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (stato) {
        case 0:
            return "default";
        case 1:
            return "info";
        case 2:
            return "primary";
        case 3:
            return "warning";
        case 4:
            return "success";
        default:
            return "default";
    }
};

// ============================================
// PROPS
// ============================================

interface OdPLegacyCardProps {
    odp: OdPLegacy;
    filtri: FiltriOdPLegacy;
}

// ============================================
// COMPONENT
// ============================================

const OdPLegacyCard = ({ odp, filtri }: OdPLegacyCardProps) => {
    const dispatch = useAppDispatch();

    const consentiModificaOperatore = useAutorizzazioneGruppi([
        PermissionTypes.PIANIFICATORI,
    ]);

    const [expanded, setExpanded] = useState(false);
    const [ostacoloDialogOpen, setOstacoloDialogOpen] = useState(false);
    const [ostacoloSelezionatoId, setOstacoloSelezionatoId] = useState<number | undefined>(undefined);

    const hasFaseTempoAnomalo = odp.fasi?.some((f) => isTempoNonAttendibile(f.ore_previste)) ?? false;

    // ============================================
    // HANDLERS
    // ============================================

    const handleSmettiDiSeguire = async () => {
        if (window.confirm(`Smettere di seguire l'OdP ${odp.esercizio}/${odp.numero_registrazione}?`)) {
            await dispatch(deleteOdPLegacy({ odpLegacyToDelete: odp }));
            dispatch(fetchOdPLegacy(filtri));
        }
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <>
        <Card
            sx={{
                mb: theme.spacing(1),
                transition: "box-shadow 0.2s ease-in-out",
                ...(hasFaseTempoAnomalo && {
                    border: "3px solid #c87acb",
                }),
                "&:hover": {
                    boxShadow: theme.shadows[4],
                },
            }}
        >
            <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                <Grid container spacing={2} sx={{ alignItems: "center" }}>
                    {/* Numero registrazione */}
                    <Grid size={{ xs: 4, md: 2, lg: 1.2 }}>
                        <Stack direction="row" spacing={0.25} sx={{ alignItems: "center", flexWrap: "nowrap" }}>
                            <Typography variant="body2" fontWeight="bold" color="primary" noWrap>
                                {odp.esercizio}/{odp.tipo_documento}/{odp.numero_registrazione}
                            </Typography>
                            {hasFaseTempoAnomalo && (
                                <Tooltip title="Almeno una fase ha tempo non attendibile">
                                    <WarningAmberIcon sx={{ fontSize: 16, color: "#e5bd0f", flexShrink: 0, display: "block" }} />
                                </Tooltip>
                            )}
                        </Stack>
                    </Grid>

                    {/* Cliente */}
                    <Grid size={{ xs: 4, md: 3, lg: 1.3 }}>
                        <Typography variant="body2" noWrap>
                            {odp.nome_cliente || "-"}
                        </Typography>
                        {/*<Typography variant="caption" color="text.secondary">*/}
                        {/*    Cod. {odp.codice_cliente_legacy}*/}
                        {/*</Typography>*/}
                    </Grid>

                    {/* Articolo */}
                    <Grid size={{ xs: 4, md: 2, lg: 1.5 }}>
                        <Typography variant="body2" noWrap>
                            {odp.codice_articolo || "-"}
                        </Typography>
                    </Grid>

                    {/* Quantità */}
                    <Grid size={{ xs: 4, md: 2, lg: 1.5 }}>
                        <Typography variant="body2">
                            {Intl.NumberFormat("it-IT", {maximumFractionDigits: 0, minimumFractionDigits: 0}).format(odp.quantita_lavorata ?? 0)} / {Intl.NumberFormat("it-IT", {maximumFractionDigits: 0, minimumFractionDigits: 0}).format(odp.quantita ?? 0)} {odp.unita_misura}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Residua: {Intl.NumberFormat("it-IT", {maximumFractionDigits: 0, minimumFractionDigits: 0}).format(odp.quantita_residua ?? 0)}
                        </Typography>
                    </Grid>

                    {/* Stato */}
                    <Grid size={{ xs: 3, md: 1, lg: 1 }}>
                        <Chip
                            label={odp.stato_display || STATI_ODP[odp.stato ?? 0] || "-"}
                            color={getStatoColor(odp.stato)}
                            size="small"
                        />
                    </Grid>

                    {/* Date pianificazione */}
                    <Grid size={{ xs: 4, md: 2, lg: 1.5 }} sx={{textAlign: "center"}}>
                        <Typography variant="body2" color="text.secondary">
                            {/*{odp.data_inizio_pianificata || "-"} → {odp.data_fine_pianificata || "-"}*/}
                            {DateTime.fromISO(odp.data_fine_pianificata || "").toFormat("dd/MM/yyyy")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            aggiornato al {convertToDateTime(odp.ultimo_sync)?.toFormat('dd LLL HH:mm')}
                        </Typography>
                    </Grid>

                    {/* Frazionamento */}
                    <Grid size={{ xs: 4, md: 2, lg: 0.7 }}>
                        <Typography variant="caption" color="text.secondary">
                            OdP quant.: {Intl.NumberFormat("it-IT", {maximumFractionDigits: 0, minimumFractionDigits: 0}).format(odp.quantita ?? 0)}
                        </Typography>
                    </Grid>

                    {/* Ostacoli */}
                    <Grid size={{ xs: 12, md: 3, lg: 3 }}>
                        <Stack direction="row" spacing={1}
                               sx={{
                                   justifyContent: "flex-end",
                                   alignItems: "center",
                               }}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(3, 95px)",
                                    gap: 0.5,
                                    justifyContent: "flex-end",
                                }}
                            >
                                {odp.ostacoli?.map((ostacolo) => {
                                    const IconComp = OSTACOLO_ICON_MAP[ostacolo.tipo || "ALT"] || HelpOutlineIcon;
                                    return (
                                        <Tooltip
                                            key={ostacolo.id}
                                            title={
                                                `${ostacolo.tipo_display}: ${ostacolo.descrizione || "-"}` +
                                                (ostacolo.is_scaduto
                                                    ? ` (scaduto ${ostacolo.giorni_ritardo}gg)`
                                                    : "")
                                            }
                                        >
                                            <Chip
                                                icon={<IconComp sx={{fontSize: 12}} />}
                                                label={ostacolo.data_prevista_risoluzione
                                                    ? DateTime.fromISO(ostacolo.data_prevista_risoluzione).toFormat("dd/MM/yyyy")
                                                    : "-"}
                                                size="small"
                                                color={
                                                    ostacolo.risolto
                                                        ? "success"
                                                        : ostacolo.is_scaduto
                                                            ? "error"
                                                            : "warning"
                                                }
                                                variant={ostacolo.risolto ? "outlined" : "filled"}
                                                sx={{height: 22, fontSize: "0.7rem", cursor: "pointer", width: "100%"}}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOstacoloSelezionatoId(ostacolo.id);
                                                    setOstacoloDialogOpen(true);
                                                }}
                                            />
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                            <Tooltip title="Aggiungi ostacolo">
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOstacoloSelezionatoId(undefined);
                                        setOstacoloDialogOpen(true);
                                    }}
                                    size="small"
                                    sx={{p: 0}}
                                >
                                    <AddCircleOutlineIcon
                                        sx={{fontSize: 18}}
                                        color={odp.ostacoli?.some((o) => !o.risolto) ? "error" : "action"}
                                    />
                                </IconButton>
                            </Tooltip>
                    {/*    </Stack>*/}
                    {/*</Grid>*/}

                    {/*/!* Actions *!/*/}
                    {/*<Grid size={{ xs: 2, md: 1, lg: 1 }}>*/}
                    {/*    <Stack direction="row" spacing={0} sx={{ justifyContent: "flex-end" }}>*/}


                            <Tooltip title="Smetti di seguire">
                                <IconButton
                                    onClick={handleSmettiDiSeguire}
                                    size="small"
                                    color="error"
                                >
                                    <LinkOffIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={expanded ? "Chiudi fasi" : "Mostra fasi"}>
                                <IconButton
                                    onClick={() => setExpanded(!expanded)}
                                    size="small"
                                >
                                    {expanded ? (
                                        <ExpandLessIcon fontSize="small" />
                                    ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Grid>
                </Grid>
            </CardContent>

            {/* Fasi espandibili */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent sx={{ pt: 0, pb: 1 }}>
                    {odp.fasi && odp.fasi.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor: theme.palette.grey[100],
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: "bold" }}>Fase</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Lavorazione</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Descrizione</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Reparto</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Operatore</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }} align="right">
                                            Qtà
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }} align="right">
                                            Lavorata
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }} align="center">
                                            Avanzamento
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Stato</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {odp.fasi.map((fase: FaseLegacy) => (
                                        <TableRow key={fase.id} hover>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                                    <span>{fase.numero_fase}</span>
                                                    {isTempoNonAttendibile(fase.ore_previste) && (
                                                        <Tooltip title="Tempo non attendibile: valore assente o inferiore alla soglia minima in eSolver">
                                                            <WarningAmberIcon sx={{ fontSize: 16, color: "warning.main" }} />
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{fase.codice_lavorazione}</TableCell>
                                            <TableCell>{fase.descrizione}</TableCell>
                                            <TableCell>
                                                {fase.reparto_display || fase.risorsa_principale || "-"}
                                            </TableCell>
                                            <TableCell>
                                                <OperatoreSelectInline
                                                    fase={fase}
                                                    odpId={odp.id!}
                                                    consentiModifica={consentiModificaOperatore}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {fase.quantita} {fase.unita_misura}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fase.quantita_lavorata}
                                            </TableCell>
                                            <TableCell align="center" sx={{ minWidth: 120 }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{ alignItems: "center" }}
                                                >
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={(Number(fase.ore_lavorate || 0) / Number(fase.ore_previste || 1)) * 100}
                                                        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                                                    />
                                                    <Typography variant="caption">
                                                        {Math.round((Number(fase.ore_lavorate || 0) / Number(fase.ore_previste || 1)) * 100)}%
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        fase.stato_display ||
                                                        STATI_ODP[fase.stato ?? 0] ||
                                                        "-"
                                                    }
                                                    color={getStatoColor(fase.stato)}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {fase.data_inizio || "-"} → {fase.data_fine || "-"}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: "center", py: 2 }}
                        >
                            Nessuna fase presente
                        </Typography>
                    )}
                </CardContent>
            </Collapse>
        </Card>

        {/* Dialog ostacolo */}
        <Dialog
            open={ostacoloDialogOpen}
            onClose={() => setOstacoloDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {ostacoloSelezionatoId ? "Modifica Ostacolo" : "Nuovo Ostacolo"}
            </DialogTitle>
            <DialogContent>
                <OstacoloForm
                    ostacoloId={ostacoloSelezionatoId}
                    odpId={odp.id}
                    onClose={() => setOstacoloDialogOpen(false)}
                />
            </DialogContent>
        </Dialog>
        </>
    );
};

export default OdPLegacyCard;